// This file is part of re-motion
// Copyright (c) rubicon IT GmbH, www.rubicon.eu
// 
import { LogManager } from "@lib/logging/LogManager";
import { IMessageFormatProvider } from "@lib/messaging/IMessageFormatProvider";
import { IMessageSender } from "@lib/messaging/IMessageSender";
import { MessageServerContext } from "@lib/messaging/MessageServerContext";
import { NetworkSource } from "@lib/messaging/NetworkSource";
import { IRpcContext } from "@lib/messaging/rpc/IRpcContext";
import { IRpcServer } from "@lib/messaging/rpc/IRpcServer";
import { IRpcContextFactory } from "@lib/messaging/rpc/jsonRpc/IRpcContextFactory";
import { JsonRpcPendingCallContainer } from "@lib/messaging/rpc/jsonRpc/JsonRpcPendingCallContainer";
import { JsonRpcRemoteRpcHub } from "@lib/messaging/rpc/jsonRpc/JsonRpcRemoteRpcHub";
import { JsonRpcRequest } from "@lib/messaging/rpc/jsonRpc/JsonRpcRequest";
import { JsonRpcResponse } from "@lib/messaging/rpc/jsonRpc/JsonRpcResponse";
import { parseJsonRpcRequest, parseJsonRpcResponse } from "@lib/messaging/rpc/jsonRpc/JsonRpcUtilities";
import { RpcError } from "@lib/messaging/rpc/RpcError";
import { RpcResult } from "@lib/messaging/rpc/RpcResult";
import { Err } from "@lib/utilities/Result";

const log = LogManager.createLog("rpc.json");

/**
 * Handles JSON-RPC request received by the message server and sends the response messages back to the sender.
 */
export class JsonRpcMessageFormatProvider<TRpcContext extends IRpcContext> implements IMessageFormatProvider
{
  private readonly pendingCalls = new JsonRpcPendingCallContainer<any, any>();
  private readonly rpcContextFactory: IRpcContextFactory<TRpcContext>;

  readonly rpcServer: IRpcServer<TRpcContext>;

  localTimeout = -1;

  constructor(rpcServer: IRpcServer<TRpcContext>, rpcContextFactory: IRpcContextFactory<TRpcContext>)
  {
    this.rpcServer = rpcServer;
    this.rpcContextFactory = rpcContextFactory;
  }

  createRemoteRpcHub(messageSender: IMessageSender)
  {
    return new JsonRpcRemoteRpcHub(this.pendingCalls, messageSender);
  }

  handleMessage(context: MessageServerContext, message: object): void
  {
    const request = parseJsonRpcRequest(message);
    if (request)
    {
      this.handleRequest(context, request);
      return;
    }

    const response = parseJsonRpcResponse(message);
    if (response)
    {
      this.handleResponse(context, response);
      return;
    }

    log.error("Invalid JSON-RPC message received: %o", message);
  }

  private handleRequest(context: MessageServerContext, request: JsonRpcRequest)
  {
    let rpcResult: RpcResult<any, any>;
    try 
    {
      const remoteRpcHub = new JsonRpcRemoteRpcHub(this.pendingCalls, context.messageSender);
      const rpcContext = this.rpcContextFactory.createRpcContext(
        context,
        {
          isNotification: request.id === undefined || request.id === null,
          remoteRpcHub: remoteRpcHub
        });

      const timeout = this.localTimeout < 0 ? undefined : this.localTimeout;
      rpcResult = this.rpcServer.invoke(
        rpcContext,
        request.method,
        request.params || [],
        timeout);
    }
    catch (ex)
    {
      log.error("Unexpected error during execution of '%s': %o", request.method, ex);
      this.sendErrorResponse(
        context.messageSender,
        context.source,
        request,
        RpcError.internal);

      return;
    }

    rpcResult.then(result => 
    {
      this.sendResponse(
        context.messageSender,
        context.source,
        request,
        result);
    }, reason => 
      {
        log.error("Unexpected error during execution of '%s': %o", request.method, reason);
        this.sendErrorResponse(
          context.messageSender,
          context.source,
          request,
          RpcError.internal);
      })
  }

  private handleResponse(context: MessageServerContext, response: JsonRpcResponse)
  {
    if (response.id === null)
    {
      if (!("error" in response))
        return;

      log.error("Received unaddressed error response: '%s' (%o)",
        response.error.message,
        response.error);

      return;
    }

    const pendingCall = this.pendingCalls.remove(response.id);
    if (!pendingCall)
      return;

    if ("error" in response)
    {
      const error = response.error;
      pendingCall.fail(RpcError.createRaw(error.code, error.message, error.data));
    }
    else
    {
      pendingCall.complete(response.result);
    }
  }

  private sendErrorResponse(messageSender: IMessageSender, destination: NetworkSource, request: JsonRpcRequest, error: RpcError<any>)
  {
    this.sendResponse(messageSender, destination, request, Err(error));
  }

  private sendResponse(messageSender: IMessageSender, destination: NetworkSource, request: JsonRpcRequest, result: Result<any, RpcError<any>>) 
  {
    const id = request.id;
    if (id === undefined || id === null)
    {
      if (result.success)
        return;

      const error = result.error;
      log.error("An error occured during handling of a notification '%s': '%s' (%o)",
        request.method,
        error.message,
        error);
      return;
    }

    let message: JsonRpcResponse;
    if (result.success)
    {
      let resultValue = result.value;

      // JSON.stringify omits undefined values so we have to explicitly use null instead
      if (resultValue === undefined)
        resultValue = null;

      message = {
        jsonrpc: "2.0",
        id: id,
        result: resultValue
      };
    }
    else
    {
      const error = result.error;
      message = {
        jsonrpc: "2.0",
        id: id,
        error: {
          code: error.code,
          data: error.data,
          message: error.message
        }
      };
    }

    messageSender.sendMessage(destination, message);
  }
}